import {
  oaDefaultRichMenu,
  richMenuIds,
  RichMenuName,
} from './chatbot.ruleset';
import { getChatbotEnv } from './chatbot.util';
import { SyncActionAPI } from '../utils/types';
import { MutableState } from '../State';
import { isGuestUser } from '../guest/guest.getters';
import ab from '../ruleset/ab';
import { getAssignOnceFn } from '../ab/ab.modifiers';
import {
  getDefaultRichMenu,
  isEligibleForCompactRichMenuTest,
} from './chatbot.getters';

type RichMenuAPI = Pick<SyncActionAPI, 'getUserID'> & {
  chatbot: Pick<
    SyncActionAPI['chatbot'],
    'linkRichMenuToUser' | 'unlinkRichMenuFromUser'
  >;
};

export function assignRichMenu(
  state: MutableState,
  richMenu: RichMenuName,
  api: RichMenuAPI,
): void {
  if (process.env.REPLICANT_OFFLINE) return;
  if (isGuestUser(state)) return;

  if (process.env.IS_DEVELOPMENT && process.env.STAGE !== 'dev') return;

  const userId = api.getUserID();
  const richMenuId = richMenuIds[getChatbotEnv()][richMenu];

  if (state.richMenuId === richMenuId) {
    // Already assigned skip linkRichMenuToUser call
    return;
  }

  // The OA default menu needs no per-user link; unlinking any other menu falls
  // back to it.
  if (richMenu === oaDefaultRichMenu) {
    if (state.richMenuId) {
      api.chatbot.unlinkRichMenuFromUser(userId);
      delete state.richMenuId;
    }
    return;
  }

  // Link rich menu to user
  api.chatbot.linkRichMenuToUser({ userId, richMenuId });

  // Remember last assigned rich menu id
  state.richMenuId = richMenuId;
}

/**
 * Assign the user's rich menu without trusting the stored ID: LINE unlinks a
 * per-user rich menu when the user blocks the OA, and an unblock arrives as a
 * follow.
 */
export function relinkRichMenu(state: MutableState, api: RichMenuAPI): void {
  delete state.richMenuId;
  assignRichMenu(state, getDefaultRichMenu(state), api);
}

/**
 * Assign an eligible OA subscriber to the compact rich menu test. Repeat
 * follows (retries, unblocks) keep the first assignment.
 */
export function enrollInCompactRichMenuTest(
  state: MutableState,
  api: Pick<SyncActionAPI, 'abTests'>,
): void {
  if (!isEligibleForCompactRichMenuTest(state)) return;

  getAssignOnceFn(state, api)(ab.tests.TEST_COMPACT_RICH_MENU);
}
